<template>
  <div
    class="fts-state"
    :class="{'fts-state--tint': tint}"
    :style="chipStyle"
    v-if="model.name"
  >
    <div>{{model.name}}</div>
  </div>
</template>

<script>
import { stateInk } from '../commons/colors';

export default {
  props: {
    stateId: String,
    state: Object,
    states: Array,
    // Opt-in calm-density variant (SS05902): the chip carries the state's hue as a tint
    // plus text-grade ink of that same hue instead of a solid fill. Off by default, so
    // every existing caller renders byte-identically.
    tint: Boolean
  },
  computed: {
    // Solid mode paints the colour straight on. Tint mode hands the colour and its two
    // themed inks to CSS as custom properties instead — CSS can mix the fill, but it
    // cannot do the contrast maths the ink needs.
    chipStyle() {
      if (!this.tint) {
        return { 'background-color': this.model.color };
      }

      const ink = stateInk(this.model.color);
      return {
        '--fts-state-color': this.model.color || null,
        '--fts-state-ink': ink && ink.light,
        '--fts-state-ink-dark': ink && ink.dark
      };
    },
    model() {
      const state = { name: '(unset)', ...this.state};
      const states = Array.isArray(this.states) ? this.states : [];
      let stateId = null;

      if (this.stateId) {
        stateId = this.stateId;
      } else if (state && state.stateId) {
        stateId = state.stateId;
      } else if (state && state.id) {
        stateId = state.id;
      }

      if (states && states.length && stateId) {
        const res = states.find(x => x.id === stateId);
        if (res && res.id) {
          return { id: res.id, name: res.name, color: res.color};
        }
      }
      return state;
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-state {
  min-width: 1rem;
  max-width: 100%;
  display: inline-flex;
  border-radius: 0.25rem;
  background-color: var(--fts-default);
  cursor: default;
  > div {
    @include text-overflow-ellipsis;
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    color: var(--fts-light);
    cursor: inherit;
  }
  &.fts-state-md {
    > div {
      font-size: $comfort-font-size;
      line-height: $comfort-line-height;
    }
  }

  // Calm-density variant: same hue, spent as a tint rather than a solid fill, so a state
  // that changed days ago stops out-shouting the content beside it. The exact configured
  // colour still reaches the reader — it is the chip's hue, and the tooltip names it.
  &.fts-state--tint {
    background-color: color-mix(in srgb, var(--fts-state-color, var(--fts-default)) 11%, transparent);

    > div {
      color: var(--fts-state-ink, var(--inistate-text-secondary));
    }
  }
}

body.dark .fts-state.fts-state--tint > div {
  color: var(--fts-state-ink-dark, var(--inistate-text-secondary));
}
</style>